import { Flex, Stack } from '@mantine/core';
import { AnimatePresence, motion } from 'motion/react';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertV2 as Alert,
  EAlertV2Type,
  Radios,
  Spinner,
  Text,
  Textarea,
  Well,
} from '@feathr/components';

import type { ISegmentAIResponse } from '../SegmentBuilderAI.controller';
import { EXAMPLE_PROMPTS, PROCESSING_STATEMENTS, renderSummary } from '../SegmentBuilderAI.utils';

import * as styles from './SegmentBuilderModalContent.css';

import pixlImage from '@feathr/extender/images/super_pixel_mascot.png';

interface IProps {
  currentStep: string;
  prompt: string;
  setPrompt: (prompt: string) => void;
  currentStatementIndex: number;
  response: ISegmentAIResponse | null;
  setSelectedExample: (example: string | undefined) => void;
  selectedExample: string | undefined;
}

function SegmentBuilderModalContent({
  currentStep,
  prompt,
  setPrompt,
  currentStatementIndex,
  response,
  setSelectedExample,
  selectedExample,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();

  // Check if this is a model failure (has summary) vs a true error
  const isModelFailure =
    !response?.success &&
    response?.summary &&
    response.summary.length > 0 &&
    !(response.summary.length === 1 && response.summary[0].content === 'Error processing response');

  const handleRadioChange = (value?: string): void => {
    setSelectedExample(value);
    if (value) {
      setPrompt(value);
    }
  };

  const renderInputStep = (): JSX.Element => (
    <motion.div
      animate={{ opacity: 1 }}
      className={styles.container}
      initial={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        initial={{ opacity: 0, y: 20 }}
        transition={{ delay: 0.1, duration: 0.4 }}
      >
        <Well layout={'vertical'}>
          <motion.div
            animate={{ opacity: 1, scale: 1 }}
            initial={{ opacity: 0, scale: 0.9 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            <Flex align={'center'} gap={8} justify={'center'}>
              <motion.img
                // TODO: update MascotAssistant so it can be used here
                alt={'Pixl AI Assistant'}
                className={styles.titleIcon}
                src={pixlImage}
                transition={{ duration: 0.2 }}
                whileHover={{ scale: 1.05 }}
              />
              <motion.div
                animate={{ opacity: 1, x: 0 }}
                className={styles.speechBubble}
                initial={{ opacity: 0, x: -10 }}
                transition={{ delay: 0.3, duration: 0.3 }}
              >
                <Text.DefaultMedium>{t("Let's build your Group together!")} 🤝</Text.DefaultMedium>
              </motion.div>
            </Flex>
          </motion.div>
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 10 }}
            transition={{ delay: 0.4, duration: 0.3 }}
          >
            <Textarea
              additionalContent={
                // Help desk article will 404 till it's published
                <Text.DefaultLink
                  href={
                    'https://help.feathr.co/hc/en-us/articles/34643693394199-What-is-the-AI-Group-Builder'
                  }
                >
                  {t('Need help writing prompts?')}
                </Text.DefaultLink>
              }
              helpText={t('Enter a custom prompt or select an example from the list below')}
              label={t('Prompt')}
              onChange={(value): void => {
                setPrompt(value || '');
                // Clear selected example if the prompt doesn't match any example
                if (!EXAMPLE_PROMPTS.includes(value || '')) {
                  setSelectedExample(undefined);
                }
              }}
              placeholder={t(
                "People who visited our donation page in the last 30 days but haven't converted yet.",
              )}
              rows={3}
              value={prompt}
            />
          </motion.div>
        </Well>
      </motion.div>
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        initial={{ opacity: 0, y: 20 }}
        transition={{ delay: 0.5, duration: 0.4 }}
      >
        <Radios
          dataName={'sample_prompt'}
          label={t('Examples')}
          layout={'block'}
          onChange={handleRadioChange}
          optional={true}
          options={EXAMPLE_PROMPTS.map((example) => ({
            id: example,
            name: example,
            theme: 'violet',
          }))}
          orientation={'vertical'}
          value={selectedExample ?? ''}
        />
      </motion.div>
    </motion.div>
  );

  const renderProcessingStep = (): JSX.Element => (
    <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }} transition={{ duration: 0.5 }}>
      <Flex align={'center'} gap={16} justify={'center'}>
        <motion.img
          alt={'Pixl Assistant'}
          animate={{
            rotate: [0, 5, -5, 0],
            scale: [1, 1.05, 1],
          }}
          className={styles.processingPixl}
          src={pixlImage}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          animate={{ scale: 1, opacity: 1 }}
          className={styles.speechBubble}
          initial={{ scale: 0.9, opacity: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 10 }}
            transition={{ delay: 0.4, duration: 0.3 }}
          >
            <Text.DefaultMedium>{t("Got it! Let's find 🔍")}</Text.DefaultMedium>
            <Text.DefaultSemibold>{prompt}</Text.DefaultSemibold>
          </motion.div>
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className={styles.loadingContainer}
            initial={{ opacity: 0, y: 10 }}
            transition={{ delay: 0.6, duration: 0.3 }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear',
              }}
            >
              <Spinner size={16} />
            </motion.div>

            <div className={styles.carouselContainer}>
              <AnimatePresence mode={'wait'}>
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  initial={{ opacity: 0, y: 5 }}
                  key={currentStatementIndex}
                  transition={{
                    duration: 0.3,
                    ease: 'easeOut',
                  }}
                >
                  <Text.DefaultMedium>
                    {PROCESSING_STATEMENTS[currentStatementIndex]}
                  </Text.DefaultMedium>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      </Flex>
    </motion.div>
  );

  // Model failure - show the AI's explanation in an error state
  const renderModelFailureStep = (): JSX.Element | undefined => {
    if (!response) {
      return;
    }

    return (
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        initial={{ opacity: 0, y: 20 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        <Alert
          className={styles.alert}
          description={renderSummary(response.summary)}
          title={t('Unable to Generate Group')}
          type={EAlertV2Type.danger}
        />
      </motion.div>
    );
  };

  // True error - show generic error message in an error state
  const renderResponseFailureStep = (): JSX.Element | undefined => {
    if (!response) {
      return;
    }

    return (
      <motion.div
        animate={{
          opacity: 1,
          y: 0,
          // Subtle scale pulse
          scale: [0.98, 1.02, 1],
        }}
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        transition={{
          delay: 0.2,
          duration: 0.5,
          scale: {
            delay: 0.4,
            duration: 0.3,
            ease: 'easeOut',
          },
        }}
      >
        <Alert
          className={styles.alert}
          description={response.error || t('An unexpected error occurred. Please try again.')}
          title={t('Error Processing Request')}
          type={EAlertV2Type.danger}
        />
      </motion.div>
    );
  };

  const renderResponseFailureStepWrapper = (): JSX.Element => {
    return (
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.4 }}
      >
        <Stack gap={16}>
          <Flex align={'center'} gap={8} justify={'center'}>
            {/* TODO: update MascotAssistant so it can be used here */}
            <motion.img
              alt={'Pixl AI Assistant'}
              animate={{
                scale: 1,
                rotate: 0,
                // Subtle shake animation
                x: [0, -2, 2, -1, 1, 0],
              }}
              className={styles.titleIcon}
              initial={{ scale: 0.8, rotate: -10 }}
              src={pixlImage}
              transition={{
                delay: 0.2,
                duration: 0.6,
                type: 'spring',
                stiffness: 200,
                x: {
                  delay: 0.4,
                  duration: 0.4,
                  ease: 'easeInOut',
                },
              }}
              whileHover={{ scale: 1.05 }}
            />
            <motion.div
              animate={{
                opacity: 1,
                x: 0,
                // Bounce effect
                scale: [0.9, 1.05, 1],
                // Subtle vertical bounce
                y: [0, -2, 0],
              }}
              className={styles.speechBubble}
              initial={{ opacity: 0, x: -20, scale: 0.9 }}
              transition={{
                delay: 0.3,
                duration: 0.5,
                type: 'spring',
                stiffness: 150,
                scale: {
                  delay: 0.5,
                  duration: 0.3,
                  ease: 'easeOut',
                },
                y: {
                  delay: 0.5,
                  duration: 0.3,
                  ease: 'easeOut',
                },
              }}
            >
              <Text.DefaultMedium>
                {t("Doh, I couldn't generate the requested Group.")}
              </Text.DefaultMedium>
              {isModelFailure && (
                <Text.DefaultMedium>{t("Let's see what went wrong.")} 🔧</Text.DefaultMedium>
              )}
            </motion.div>
          </Flex>
          {isModelFailure ? renderModelFailureStep() : renderResponseFailureStep()}
        </Stack>
      </motion.div>
    );
  };

  const renderResultsStep = (): JSX.Element => {
    if (!response) {
      return <div>{t('No response received')}</div>;
    }

    if (!response.success) {
      return renderResponseFailureStepWrapper();
    }

    return (
      <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }} transition={{ duration: 0.5 }}>
        <Stack>
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            transition={{ delay: 0.1, duration: 0.4 }}
          >
            <Flex align={'center'} gap={8} justify={'center'}>
              {/* TODO: update MascotAssistant to be used here */}
              <motion.img
                alt={'Pixl AI Assistant'}
                animate={{ scale: 1, rotate: 0 }}
                className={styles.titleIcon}
                initial={{ scale: 0.8, rotate: -10 }}
                src={pixlImage}
                transition={{
                  delay: 0.2,
                  duration: 0.5,
                  type: 'spring',
                  stiffness: 200,
                }}
                whileHover={{ scale: 1.05 }}
              />
              <motion.div
                animate={{ opacity: 1, x: 0, scale: 1 }}
                className={styles.speechBubble}
                initial={{ opacity: 0, x: -20, scale: 0.9 }}
                transition={{
                  delay: 0.3,
                  duration: 0.4,
                  type: 'spring',
                  stiffness: 150,
                }}
              >
                <Text.DefaultMedium>{t('Ta-da!')} 🎉</Text.DefaultMedium>
                <Text.DefaultMedium>
                  {t('Here is the group I generated for you.')}
                </Text.DefaultMedium>
              </motion.div>
            </Flex>
          </motion.div>

          <motion.div
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            transition={{ delay: 0.5, duration: 0.4 }}
          >
            <Well label={t('Summary')} layout={'vertical'}>
              <motion.div
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ delay: 0.7, duration: 0.3 }}
              >
                {renderSummary(response.summary)}
              </motion.div>
            </Well>
          </motion.div>

          <motion.div
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            transition={{ delay: 0.8, duration: 0.4 }}
          >
            <Alert
              className={styles.alert}
              description={t(
                'Applying this group will replace your current filters, matching mode, and time range settings.',
              )}
              title={t('Current Filters Will Be Overwritten')}
              type={EAlertV2Type.warning}
            />
          </motion.div>
        </Stack>
      </motion.div>
    );
  };

  const getModalContent = (): JSX.Element => {
    return (
      <AnimatePresence mode={'wait'}>
        {currentStep === 'input' && (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            initial={{ opacity: 0, y: 20 }}
            key={'input'}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            {renderInputStep()}
          </motion.div>
        )}

        {currentStep === 'processing' && (
          <motion.div
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.05 }}
            initial={{ opacity: 0, scale: 0.95 }}
            key={'processing'}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          >
            {renderProcessingStep()}
          </motion.div>
        )}

        {currentStep === 'results' && (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            initial={{ opacity: 0, y: 20 }}
            key={'results'}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            {renderResultsStep()}
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  return getModalContent();
}

export default SegmentBuilderModalContent;
